import { useEffect, useState } from 'react';
import { loadRemote } from '@module-federation/enhanced/runtime';
import { BACKEND_BASE_URL } from '../constants/api';
import ccoeCommon from './components/ccoe-common';
import { registerMfeRemote } from './remotes';

export interface CcoeAdminState {
  isAdmin: boolean;
  isResolving: boolean;
}

// Only the latest JWT's result is kept, so the cache can't grow.
let cached: { jwt: string; isAdmin: boolean } | null = null;
const getCached = (jwt: string): boolean | undefined =>
  cached?.jwt === jwt ? cached.isAdmin : undefined;

const fetchIsAdmin = async (jwt: string): Promise<boolean> => {
  registerMfeRemote(ccoeCommon);
  const common: any = await loadRemote(`${ccoeCommon.remote}/index`);
  const { discoveryApi, fetchApi, identityApi } = common.createMfePorts(
    jwt,
    BACKEND_BASE_URL,
  );
  const [profile, baseUrl] = await Promise.all([
    identityApi.getProfileInfo(),
    discoveryApi.getBaseUrl('cloud-coe-plugin-onboarding'),
  ]);
  if (!profile.email) return false;
  const email = profile.email.toLowerCase();

  const response = await fetchApi.fetch(`${baseUrl}/admins`);
  if (!response.ok) return false;
  const adminList = await response.json();
  return (
    Array.isArray(adminList) &&
    adminList.some(a => typeof a === 'string' && a.toLowerCase() === email)
  );
};

export default function useIsCcoeAdmin(jwt: string | null | undefined): CcoeAdminState {
  const [state, setState] = useState<CcoeAdminState>(() => {
    const hit = jwt ? getCached(jwt) : undefined;
    return hit === undefined
      ? { isAdmin: false, isResolving: true }
      : { isAdmin: hit, isResolving: false };
  });

  useEffect(() => {
    if (!jwt) {
      setState({ isAdmin: false, isResolving: false });
      return undefined;
    }
    const hit = getCached(jwt);
    if (hit !== undefined) {
      setState({ isAdmin: hit, isResolving: false });
      return undefined;
    }

    setState(prev => ({ ...prev, isResolving: true }));
    let cancelled = false;
    fetchIsAdmin(jwt)
      .then(isAdmin => {
        if (!cancelled) {
          cached = { jwt, isAdmin };
          setState({ isAdmin, isResolving: false });
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error resolving CCoE admin status:', error);
        if (!cancelled) setState({ isAdmin: false, isResolving: false });
      });
    return () => {
      cancelled = true;
    };
  }, [jwt]);

  return state;
}
